/**
 * Recursive component for rendering nested JSON arguments as editable nested fields
 * Displays nested objects/arrays with proper indentation and allows editing individual nested fields
 */

import React from 'react';
import { Box, TextField, Typography } from '@mui/material';
import CustomSwitch from '../../components/CustomSwitch';
import LayeredAutocompleteSelect from '../../components/LayeredAutocompleteSelect';
import {
  datetimeFromInput,
  datetimeInput,
  descriptorLabel,
  descriptorReadonly,
  formatArgLabel,
  localTimeZoneName,
  normalizeFieldDescriptor,
  getEffectiveType,
  sortEntriesByIndex,
} from '../utils/argumentUtils';
import { SCHEDULE_REF_PATTERN } from '../utils/scheduleRefs';
import { renderFormattedContent, hasHtmlTags } from '../../utils/htmlUtils';
import { HitlUpdatedFieldFrame } from './HitlUpdatedFieldFrame';
import { NEUTRAL, RADIUS, VIOLET } from '../../styles/brandTokens';
import { getValueAtPath, isUpdatedArgPath, joinArgPath } from '../hitl/hitlArgDiff';

/**
 * Roughly one line of text in our default full-width field (~14px, typical panel width).
 * Above this length, a single-line control would clip or require horizontal scroll — use multiline.
 */
const APPROX_CHARS_BEFORE_WRAP = 52;
/**
 * Multiline when the value would wrap: newlines, long unbroken line, or HTML (needs vertical space).
 */
function hasOwnResolvedDisplay(resolvedValue: unknown, fieldValue: unknown): boolean {
  if (resolvedValue === null || resolvedValue === undefined || typeof resolvedValue === 'object') return false;
  return String(resolvedValue) !== String(fieldValue ?? '');
}

function contentNeedsMultiline(valueToDisplay: unknown): boolean {
  const s = String(valueToDisplay ?? '');
  if (!s) return false;
  if (/\r|\n/.test(s)) return true;
  if (hasHtmlTags(s)) return true;
  return s.length > APPROX_CHARS_BEFORE_WRAP;
}

function getScheduleReferencePath(value: unknown): string | null {
  if (typeof value === 'string' && SCHEDULE_REF_PATTERN.test(value)) {
    const exact = value.match(/^\$\{\s*([^}]+?)\s*\}$/);
    return exact ? exact[1] : value;
  }
  return null;
}

interface NestedArgFieldProps {
  fieldKey: string;
  fieldValue: any;
  fieldPath: string; // Top-level key like "email_input" - used to identify which object is being updated
  fieldType?: any; // Structured descriptor or nested type map
  isResolved: boolean;
  disabled: boolean;
  displayValue?: string; // Display name for the field
  onValueChange: (topLevelKey: string, updatedNestedObject: any) => void; // Passes top-level key and full updated nested object
  resolvedValue?: any; // Resolved value if different from fieldValue
  depth?: number; // Nesting depth for indentation
  fontSizes?: { body: number };
  /** When true, Autocomplete uses inline popper (`disablePortal`) so dropdowns stack correctly in the browser extension. */
  isExtension?: boolean;
  /** Resolves dependency for depends_on: given a field key, returns its value in the current scope (for conditional visibility). */
  getDependencyValue?: (key: string) => any;
  referenceOptions?: Array<{ label: string; value: string }>;
  /** Dotted HITL argument path for this field, e.g. transfer_input.destination_user_id */
  valuePath?: string;
  /** HITL updated argument paths from message processing; empty while the card is still editable. */
  updatedArgPaths?: string[];
  /** Unwrapped original HITL args used to render the previous-value caption. */
  originalArgs?: Record<string, unknown> | null;
}

const NestedArgField: React.FC<NestedArgFieldProps> = ({
  fieldKey,
  fieldValue,
  fieldPath,
  fieldType,
  isResolved,
  disabled: disabledProp,
  displayValue,
  onValueChange,
  resolvedValue,
  depth = 0,
  fontSizes = { body: 14 },
  isExtension = false,
  getDependencyValue,
  referenceOptions = [],
  valuePath,
  updatedArgPaths = [],
  originalArgs = null,
}) => {
  const indent = depth * 24; // 24px per nesting level
  // A readonly descriptor (e.g. the record id an update targets) is shown but never edited.
  const disabled = disabledProp || descriptorReadonly(fieldType);
  const displayName = displayValue || descriptorLabel(fieldType) || formatArgLabel(fieldKey);
  const currentValuePath = valuePath || fieldKey;
  const isUpdated = isUpdatedArgPath(updatedArgPaths, currentValuePath);
  const previousValue = isUpdated ? getValueAtPath(originalArgs, currentValuePath) : undefined;
  // A leaf shows its own display value. A parent object being resolved must not turn
  // unresolved siblings green or replace them. Nested objects stay on fieldValue so
  // each child can resolve independently.
  const resolvedLeaf = hasOwnResolvedDisplay(resolvedValue, fieldValue) ? resolvedValue : undefined;
  const valueToDisplay = resolvedLeaf !== undefined ? resolvedLeaf : fieldValue;
  const scheduleReferencePath = getScheduleReferencePath(fieldValue);
  const currentReferenceLabel =
    scheduleReferencePath &&
    (referenceOptions.find((option) => option.value === scheduleReferencePath)?.label || scheduleReferencePath);

  const normalizeToNewlines = (input: string): string => {
    return input
      .replace(/<br\s*\/?>/gi, "\n")
      .replace(/\\n/g, "\n")
      .replace(/\r\n/g, "\n")
      .replace(/\r/g, "\n");
  };

  const htmlBodyToEditableText = (html: string): string => {
    if (!html) return "";
    const withLineBreaks = html
      .replace(/<\/li>\s*<li>/gi, "\n")
      .replace(/<li>/gi, "• ")
      .replace(/<\/p>\s*<p>/gi, "\n\n")
      .replace(/<br\s*\/?>/gi, "\n")
      .replace(/<\/div>\s*<div>/gi, "\n");
    const withoutTags = withLineBreaks.replace(/<\/?[^>]+>/g, "");
    if (typeof document !== "undefined") {
      const textarea = document.createElement("textarea");
      textarea.innerHTML = withoutTags;
      return textarea.value;
    }
    return withoutTags
      .replace(/&nbsp;/g, " ")
      .replace(/&amp;/g, "&")
      .replace(/&lt;/g, "<")
      .replace(/&gt;/g, ">");
  };

  const editableTextToHtmlBody = (text: string): string => {
    if (!text) return "";
    const escaped = text
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");
    return escaped
      .split("\n")
      .map((line) => line.trim() === "" ? "<br />" : line)
      .join("<br />");
  };

  if (scheduleReferencePath) {
    return (
      <Box mb={depth === 0 ? 2 : 1.5} sx={{ ml: `${indent}px` }}>
        <Box display="flex" flexDirection="column" gap={1}>
          <Box sx={{ minWidth: '120px', mr: 2 }}>
            <Typography
              fontSize={fontSizes.body}
              fontWeight={500}
              color="#000000"
              fontFamily="var(--theo-font-sans)"
            >
              {displayName}:
            </Typography>
          </Box>
          <TextField
            fullWidth
            size="small"
            value={currentReferenceLabel || 'Scheduled output'}
            disabled
            helperText="Resolved when the schedule runs"
            sx={{
              '& .MuiOutlinedInput-root': {
                p: '5px 10px !important',
                borderRadius: `${RADIUS.control}px`,
                border: `1px solid ${VIOLET.tint}`,
                backgroundColor: VIOLET.wash,
              },
              '& .MuiInputBase-input': {
                color: VIOLET.dark,
                fontSize: `${fontSizes.body}px`,
                fontFamily: 'var(--theo-font-sans)',
                lineHeight: '20px',
              },
              '& .MuiInputLabel-root': {
                fontSize: `${fontSizes.body}px`,
                fontFamily: 'var(--theo-font-sans)',
              },
              '& .MuiInputBase-input.Mui-disabled': {
                WebkitTextFillColor: VIOLET.dark,
              },
            }}
          />
        </Box>
      </Box>
    );
  }

  // Handle nested objects
  if (fieldValue !== null && typeof fieldValue === 'object' && !Array.isArray(fieldValue)) {
    const objectValue = fieldValue as Record<string, any>;
    const getNestedDescriptor = (k: string) =>
      fieldType && typeof fieldType === 'object' && !Array.isArray(fieldType) ? (fieldType as any)[k] : undefined;
    const sortedEntries = sortEntriesByIndex(Object.entries(objectValue), getNestedDescriptor);
    return (
      <Box mb={depth === 0 ? 2 : 1.5} sx={{ ml: `${indent}px` }}>
        <Typography
          fontSize={fontSizes.body}
          fontWeight={600}
          color="#000000"
          fontFamily="var(--theo-font-sans)"
          sx={{ mb: 1 }}
        >
          {displayName}:
        </Typography>
        <Box
          sx={{
            borderLeft: depth > 0 ? `2px solid ${NEUTRAL.border}` : 'none',
            pl: depth > 0 ? 2 : 0,
            ml: depth > 0 ? 1 : 0,
          }}
        >
          {sortedEntries.map(([nestedKey, nestedValue]) => {
            const nestedResolvedValue = resolvedValue && typeof resolvedValue === 'object' && resolvedValue !== null
              ? (resolvedValue as any)[nestedKey]
              : undefined;
            const nestedFieldType =
              fieldType && typeof fieldType === 'object' && !Array.isArray(fieldType)
                ? (fieldType as any)[nestedKey]
                : undefined;
            
            // Check if nested field is resolved independently
            const nestedIsResolved = hasOwnResolvedDisplay(nestedResolvedValue, nestedValue);
            
            return (
              <NestedArgField
                key={nestedKey}
                fieldKey={nestedKey}
                fieldValue={nestedValue}
                fieldPath={fieldPath}
                fieldType={nestedFieldType}
                isResolved={nestedIsResolved}
                disabled={disabled || nestedIsResolved}
                onValueChange={(topLevelKey, updatedValue) => {
                  const updatedNestedObject = { ...objectValue, [nestedKey]: updatedValue };
                  onValueChange(topLevelKey, updatedNestedObject);
                }}
                resolvedValue={nestedResolvedValue}
                depth={depth + 1}
                fontSizes={fontSizes}
                isExtension={isExtension}
                getDependencyValue={(k) => objectValue[k]}
                referenceOptions={referenceOptions}
                valuePath={joinArgPath(currentValuePath, nestedKey)}
                updatedArgPaths={updatedArgPaths}
                originalArgs={originalArgs}
              />
            );
          })}
        </Box>
      </Box>
    );
  }

  // A list of objects — one readable block per entry, rather than one box of raw JSON.
  //
  // A batch of console actions arrives this way: five invitations is five {action_name, params}
  // objects, and a technician being asked to approve them should be able to read them. Rendered
  // as JSON they are one grey textarea to squint at, which is how every array rendered before
  // there was anything in the product that sent one worth reading.
  //
  // Only for arrays whose entries are all plain objects. Anything else — scalars, mixed, empty —
  // keeps the JSON box below, which stays the honest answer for a shape we cannot lay out.
  if (
    Array.isArray(valueToDisplay) &&
    valueToDisplay.length > 0 &&
    valueToDisplay.every((entry) => entry !== null && typeof entry === 'object' && !Array.isArray(entry))
  ) {
    const entries = valueToDisplay as Array<Record<string, any>>;
    const replaceEntry = (index: number, updated: Record<string, any>) => {
      const next = entries.map((entry, position) => (position === index ? updated : entry));
      onValueChange(fieldPath, next);
    };

    return (
      <Box mb={depth === 0 ? 2 : 1.5} sx={{ ml: `${indent}px` }}>
        <HitlUpdatedFieldFrame isUpdated={isUpdated} previousValue={previousValue} fontSize={fontSizes.body}>
          <Typography
            fontSize={fontSizes.body}
            fontWeight={isUpdated ? 600 : 500}
            color="#000000"
            fontFamily="var(--theo-font-sans)"
            mb={0.5}
          >
            {displayName} ({entries.length}):
          </Typography>
          {entries.map((entry, index) => (
            <Box
              key={`${fieldKey}-${index}`}
              mb={1}
              sx={{ borderLeft: `2px solid ${NEUTRAL.border}`, pl: 1.5 }}
            >
              <Typography fontSize={fontSizes.body - 1} fontWeight={600} color={NEUTRAL.textSecondary} fontFamily="var(--theo-font-sans)">
                {index + 1}.
              </Typography>
              {sortEntriesByIndex(Object.entries(entry), () => undefined).map(([key, value]) => (
                <NestedArgField
                  key={key}
                  fieldKey={key}
                  fieldValue={value}
                  fieldPath={fieldPath}
                  isResolved={isResolved}
                  disabled={disabled}
                  onValueChange={(_topLevelKey, updatedValue) => replaceEntry(index, { ...entry, [key]: updatedValue })}
                  depth={depth + 1}
                  fontSizes={fontSizes}
                  isExtension={isExtension}
                  referenceOptions={referenceOptions}
                  valuePath={joinArgPath(currentValuePath, `${index}.${key}`)}
                  updatedArgPaths={updatedArgPaths}
                  originalArgs={originalArgs}
                />
              ))}
            </Box>
          ))}
        </HitlUpdatedFieldFrame>
      </Box>
    );
  }

  // Handle arrays — multiline + pretty JSON when long
  if (Array.isArray(valueToDisplay)) {
    const jsonCompact = JSON.stringify(valueToDisplay);
    const jsonPretty = JSON.stringify(valueToDisplay, null, 2);
    const arrayMultiline =
      jsonCompact.length > APPROX_CHARS_BEFORE_WRAP || jsonPretty.split('\n').length > 1;

    return (
      <Box mb={depth === 0 ? 2 : 1.5} sx={{ ml: `${indent}px` }}>
        <HitlUpdatedFieldFrame isUpdated={isUpdated} previousValue={previousValue} fontSize={fontSizes.body}>
        <Box display="flex" flexDirection="column" gap={1}>
          <Typography
            fontSize={fontSizes.body}
            fontWeight={isUpdated ? 600 : 500}
            color="#000000"
            fontFamily="var(--theo-font-sans)"
            sx={{ minWidth: '120px' }}
          >
            {displayName}:
          </Typography>
          <TextField
            fullWidth
            size="small"
            multiline={arrayMultiline}
            minRows={arrayMultiline ? 3 : undefined}
            maxRows={arrayMultiline ? 18 : undefined}
            value={arrayMultiline ? jsonPretty : jsonCompact}
            disabled={disabled}
            onChange={(e) => {
              if (!disabled) {
                try {
                  const parsed = JSON.parse(e.target.value);
                  if (Array.isArray(parsed)) {
                    onValueChange(fieldPath, parsed);
                  }
                } catch {
                  // Invalid JSON - user might be editing
                }
              }
            }}
            sx={{
              color: isResolved ? '#4CAF50 !important' : `${NEUTRAL.textSecondary} !important`,
              fontSize: `${fontSizes.body}px`,
              fontFamily: 'var(--theo-font-sans) !important',
              fontWeight: 400,
              '& .MuiOutlinedInput-root': {
                p: '5px 10px !important',
                borderRadius: `${RADIUS.control}px`,
                border: `1px solid ${NEUTRAL.borderStrong}`,
                backgroundColor: NEUTRAL.surface,
                color: `${NEUTRAL.textSecondary}`,
                alignItems: arrayMultiline ? 'flex-start' : undefined,
                '& fieldset': {
                  borderColor: isResolved ? '#4CAF50' : 'transparent !important',
                  borderWidth: isResolved ? '1px' : '0px',
                },
                '&.Mui-focused': { borderColor: VIOLET.main },
                '&.Mui-disabled': {
                  backgroundColor: NEUTRAL.subtle,
                  color: `${NEUTRAL.textSecondary}`,
                },
              },
              '& .MuiInputBase-input': {
                color: isResolved ? '#4CAF50' : `${NEUTRAL.textSecondary}`,
                fontSize: `${fontSizes.body}px`,
                fontFamily: 'var(--theo-font-sans)',
                lineHeight: '20px',
                ...(arrayMultiline && {
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  overflowY: 'auto',
                }),
              },
              '& .MuiInputBase-input.Mui-disabled': {
                WebkitTextFillColor: `${NEUTRAL.textSecondary}`,
              },
            }}
          />
        </Box>
        </HitlUpdatedFieldFrame>
      </Box>
    );
  }

  // Normalize descriptor ({ type, index, description, depends_on }).
  const descriptor = normalizeFieldDescriptor(fieldType);
  const effectiveType = getEffectiveType(descriptor);
  if (descriptor.depends_on != null && getDependencyValue) {
    const depVal = getDependencyValue(descriptor.depends_on);
    if (!depVal) return null; // Hide field when dependency is not satisfied
  }

  const isBool = effectiveType === 'bool' || typeof valueToDisplay === 'boolean';
  const isDecimal = effectiveType === 'decimal';
  const isDatetime = effectiveType === 'datetime';
  const isSelect =
    effectiveType !== null &&
    typeof effectiveType === 'object' &&
    (effectiveType as any).type === 'select' &&
    Array.isArray((effectiveType as any).options);
  const selectOptions = isSelect ? ((effectiveType as any).options as any[]) : [];
  const normalizedOptions = selectOptions.map((opt) =>
    typeof opt === 'object' && opt !== null
      ? {
          value: String((opt as any).value ?? ''),
          label: String((opt as any).label ?? (opt as any).value ?? ''),
        }
      : { value: String(opt), label: String(opt) }
  );
  const hasDescription = Boolean(descriptor.description && descriptor.description.trim());

  const fieldKeyLower = String(fieldKey || "").toLowerCase();
  const isBodyLikeField =
    fieldKeyLower === "body" ||
    fieldKeyLower.endsWith("_body") ||
    fieldKeyLower.includes("email_body");
  const bodyTypeRaw =
    getDependencyValue?.("body_type") ??
    getDependencyValue?.("bodyType") ??
    getDependencyValue?.("bodytype");
  const isHtmlBodyField =
    isBodyLikeField &&
    typeof bodyTypeRaw === "string" &&
    bodyTypeRaw.trim().toLowerCase() === "html";

  const rawTextValue = String(valueToDisplay ?? "");
  const uiTextValue = isHtmlBodyField ? htmlBodyToEditableText(rawTextValue) : rawTextValue;
  const normalizedForDetection = normalizeToNewlines(uiTextValue);
  const lineCount = normalizedForDetection ? normalizedForDetection.split("\n").length : 1;
  const shouldBeMultiline =
    !isBool &&
    !isDecimal &&
    !isDatetime &&
    !isSelect &&
    (isHtmlBodyField
      ? lineCount > 1 || normalizedForDetection.length > 120
      : contentNeedsMultiline(valueToDisplay));
  const multilineMinRows = shouldBeMultiline
    ? isHtmlBodyField
      ? Math.min(6, lineCount > 1 ? lineCount : 4)
      : 4
    : undefined;

  return (
    <Box mb={depth === 0 ? 2 : 1.5} sx={{ ml: `${indent}px` }}>
      <HitlUpdatedFieldFrame isUpdated={isUpdated} previousValue={previousValue} fontSize={fontSizes.body}>
      <Box
        display="flex"
        flexDirection={isBool ? 'row' : 'column'}
        justifyContent="space-between"
        gap={1}
      >
        <Box sx={{ minWidth: '120px', mr: 2 }}>
          <Typography
            fontSize={fontSizes.body}
            fontWeight={isUpdated ? 600 : 500}
            color="#000000"
            fontFamily="var(--theo-font-sans)"
          >
            {displayName}
            {descriptor.required && (
              <Box component="span" aria-label="required" sx={{ color: '#D32F2F', ml: 0.25 }}>
                *
              </Box>
            )}
            :
          </Typography>
          {hasDescription && (
            <Typography
              component="span"
              fontSize={fontSizes.body - 2}
              fontWeight={400}
              color={NEUTRAL.textSecondary}
              fontFamily="var(--theo-font-sans)"
              sx={{ display: 'block', mt: 0.25 }}
            >
              {descriptor.description}
            </Typography>
          )}
          {descriptor.before !== undefined && (
            <Typography
              component="span"
              fontSize={fontSizes.body - 2}
              fontWeight={400}
              color={NEUTRAL.textSecondary}
              fontFamily="var(--theo-font-sans)"
              sx={{ display: 'block', mt: 0.25 }}
            >
              Currently: {descriptor.before}
            </Typography>
          )}
        </Box>
        {isBool ? (
          <>
            <CustomSwitch
              checked={Boolean(valueToDisplay)}
              disabled={disabled}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                if (!disabled) {
                  onValueChange(fieldPath, e.target.checked);
                }
              }}
            />
          </>
        ) : isSelect ? (
          <>
            <LayeredAutocompleteSelect
              label=""
              value={String(valueToDisplay ?? (normalizedOptions[0]?.value ?? ''))}
              options={normalizedOptions}
              onChange={(next) => {
                if (!disabled) {
                  onValueChange(fieldPath, next);
                }
              }}
              disabled={disabled}
              disableClearable
              disablePortal={isExtension}
              listboxMaxHeight={420}
              textFieldSx={{
                '& .MuiInputLabel-root': { display: 'none' },
                '& .MuiOutlinedInput-root': {
                  p: '5px 10px !important',
                  borderRadius: `${RADIUS.control}px`,
                  border: `1px solid ${NEUTRAL.borderStrong}`,
                  backgroundColor: NEUTRAL.surface,
                  color: `${NEUTRAL.textSecondary}`,
                  '& fieldset': {
                    borderColor: isResolved ? '#4CAF50' : 'transparent !important',
                    borderWidth: isResolved ? '1px' : '0px',
                  },
                  '&.Mui-focused': { borderColor: VIOLET.main },
                  '&.Mui-disabled': {
                    backgroundColor: NEUTRAL.subtle,
                    color: `${NEUTRAL.textSecondary}`,
                  },
                },
                '& .MuiInputBase-input': {
                  color: isResolved ? '#4CAF50' : `${NEUTRAL.textSecondary}`,
                  fontSize: `${fontSizes.body}px`,
                  fontFamily: 'var(--theo-font-sans)',
                  lineHeight: '20px',
                },
              }}
            />
          </>
        ) : isDatetime ? (
          <>
            <TextField
              fullWidth
              size="small"
              type={datetimeInput(valueToDisplay).type}
              value={datetimeInput(valueToDisplay).value}
              helperText={datetimeInput(valueToDisplay).zoned ? `Your time (${localTimeZoneName()})` : undefined}
              disabled={disabled}
              onChange={(e) => {
                if (!disabled) {
                  onValueChange(fieldPath, datetimeFromInput(e.target.value, valueToDisplay));
                }
              }}
              InputLabelProps={{ shrink: true }}
              sx={{
                color: isResolved ? '#4CAF50 !important' : `${NEUTRAL.textSecondary} !important`,
                fontSize: `${fontSizes.body}px`,
                fontFamily: 'var(--theo-font-sans) !important',
                fontWeight: 400,
                '& .MuiOutlinedInput-root': {
                  p: '5px 10px !important',
                  borderRadius: `${RADIUS.control}px`,
                  border: `1px solid ${NEUTRAL.borderStrong}`,
                  backgroundColor: NEUTRAL.surface,
                  color: `${NEUTRAL.textSecondary}`,
                  '& fieldset': {
                    borderColor: isResolved ? '#4CAF50' : 'transparent !important',
                    borderWidth: isResolved ? '1px' : '0px',
                  },
                  '&.Mui-focused': { borderColor: VIOLET.main },
                  '&.Mui-disabled': {
                    backgroundColor: NEUTRAL.subtle,
                    color: `${NEUTRAL.textSecondary}`,
                  },
                },
                '& .MuiInputBase-input': {
                  color: isResolved ? '#4CAF50' : `${NEUTRAL.textSecondary}`,
                  fontSize: `${fontSizes.body}px`,
                  fontFamily: 'var(--theo-font-sans)',
                  lineHeight: '20px',
                },
                '& .MuiInputBase-input.Mui-disabled': {
                  WebkitTextFillColor: `${NEUTRAL.textSecondary}`,
                },
              }}
            />
          </>
        ) : disabled && shouldBeMultiline ? (
          <Box
            component="div"
            sx={{
              width: '100%',
              boxSizing: 'border-box',
              p: '10px 12px',
              borderRadius: `${RADIUS.control}px`,
              border: isResolved ? '1px solid #4CAF50' : `1px solid ${NEUTRAL.borderStrong}`,
              backgroundColor: NEUTRAL.subtle,
              color: `${NEUTRAL.textSecondary}`,
              fontSize: `${fontSizes.body}px`,
              fontFamily: 'var(--theo-font-sans)',
              fontWeight: 400,
              minHeight: 96,
              maxHeight: 320,
              overflow: 'auto',
              wordBreak: 'break-word',
              '& p': { margin: '0 0 0.75em 0' },
              '& p:last-child': { marginBottom: 0 },
              '& ul, & ol': { margin: '0 0 0.75em 0', paddingLeft: '1.25rem' },
            }}
            dangerouslySetInnerHTML={{
              __html: renderFormattedContent(String(valueToDisplay ?? '')),
            }}
          />
        ) : (
          <>
            <TextField
              fullWidth
              size="small"
              type={isDecimal ? 'number' : 'text'}
              inputProps={isDecimal ? { step: '0.25', min: 0 } : undefined}
              value={uiTextValue}
              multiline={shouldBeMultiline}
              minRows={multilineMinRows}
              maxRows={shouldBeMultiline ? 18 : undefined}
              disabled={disabled}
              onChange={(e) => {
                if (!disabled) {
                  const numValue = Number(e.target.value);
                  let finalValue: any;
                  if (isDecimal && e.target.value.trim() !== '' && !isNaN(numValue)) {
                    finalValue = numValue;
                  } else if (isHtmlBodyField) {
                    finalValue = editableTextToHtmlBody(e.target.value);
                  } else {
                    finalValue = e.target.value;
                  }
                  onValueChange(fieldPath, finalValue);
                }
              }}
              sx={{
                color: isResolved ? '#4CAF50 !important' : `${NEUTRAL.textSecondary} !important`,
                fontSize: `${fontSizes.body}px`,
                fontFamily: 'var(--theo-font-sans) !important',
                fontWeight: 400,
                '& .MuiOutlinedInput-root': {
                  p: '5px 10px !important',
                  borderRadius: `${RADIUS.control}px`,
                  border: `1px solid ${NEUTRAL.borderStrong}`,
                  backgroundColor: NEUTRAL.surface,
                  color: `${NEUTRAL.textSecondary}`,
                  alignItems: shouldBeMultiline ? 'flex-start' : undefined,
                  '& fieldset': {
                    borderColor: isResolved ? '#4CAF50' : 'transparent !important',
                    borderWidth: isResolved ? '1px' : '0px',
                  },
                  '&.Mui-focused': { borderColor: VIOLET.main },
                  '&.Mui-disabled': {
                    backgroundColor: NEUTRAL.subtle,
                    color: `${NEUTRAL.textSecondary}`,
                  },
                },
                '& .MuiInputBase-input': {
                  color: isResolved ? '#4CAF50' : `${NEUTRAL.textSecondary}`,
                  fontSize: `${fontSizes.body}px`,
                  fontFamily: 'var(--theo-font-sans)',
                  lineHeight: '20px',
                  ...(shouldBeMultiline && {
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    overflowY: 'auto',
                  }),
                },
                '& .MuiInputBase-input.Mui-disabled': {
                  WebkitTextFillColor: `${NEUTRAL.textSecondary}`,
                },
              }}
            />
          </>
        )}
      </Box>
      </HitlUpdatedFieldFrame>
    </Box>
  );
};

export default NestedArgField;
